/**
 * Business Map Component
 * 업체 위치 지도 표시
 */

import { useEffect, useRef, useState } from "react";

interface BusinessMapProps {
  lat: number;
  lng: number;
  address: string;
  name?: string | null;
}

declare global {
  interface Window {
    google: any;
  }
}

export function BusinessMap({ lat, lng, address, name }: BusinessMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);

  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google?.maps) {
      setMapsLoaded(true);
      return;
    }

    // Use centralized Google Maps loader
    import("@/lib/googleMapsLoader")
      .then(({ loadGoogleMaps }) => {
        loadGoogleMaps()
          .then(() => {
            setMapsLoaded(true);
          })
          .catch((error) => {
            console.warn("Failed to load Google Maps:", error);
          });
      })
      .catch((error) => {
        console.error("Failed to import googleMapsLoader:", error);
      });
  }, []);

  useEffect(() => {
    if (!mapRef.current || !mapsLoaded || !window.google?.maps) return;

    try {
      // Map과 Marker가 사용 가능한지 확인
      const Map = window.google.maps.Map;
      const Marker = window.google.maps.Marker;

      if (!Map || !Marker) {
        console.warn("Google Maps Map or Marker not available");
        return;
      }

      const fallbackPosition = { lat, lng };
      const map = new Map(mapRef.current, {
        center: fallbackPosition,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
      });

      const marker = new Marker({
        position: fallbackPosition,
        map,
        title: address,
      });

      // Imported coordinates can be a street/building centroid. Resolve the
      // public business name + address first and retain coordinates as fallback.
      const placeQuery = [name, address].filter(Boolean).join(", ");
      if (placeQuery && window.google.maps.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: placeQuery, region: "AU" }, (results: any[], status: string) => {
          const resolvedPosition = results?.[0]?.geometry?.location;
          if (status === "OK" && resolvedPosition) {
            marker.setPosition(resolvedPosition);
            map.setCenter(resolvedPosition);
          }
        });
      }
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }, [lat, lng, address, name, mapsLoaded]);

  return (
    <div className="w-full h-64 rounded-lg overflow-hidden border">
      <div ref={mapRef} className="w-full h-full" />
      {!mapsLoaded && (
        <div className="flex items-center justify-center h-full bg-muted -mt-64 relative">
          <p className="text-muted-foreground">지도를 불러오는 중...</p>
        </div>
      )}
    </div>
  );
}
